import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { pprwAPI } from '../../services/api';

const fmtDate = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }); } catch { return d; } };

// Pick standalone (unassigned) sessions to add to a workshop.
export function AddSessionsDialog({ open, onClose, workshopId, onAdded }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = await pprwAPI.getAll();
      setSessions(all.filter(s => !s.workshop_id));
    } catch (_e) { toast.error('Failed to load sessions'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) { setSelected(new Set()); setSearch(''); load(); } }, [open, load]);

  const toggle = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const filtered = search
    ? sessions.filter(s => (s.course_title || '').toLowerCase().includes(search.toLowerCase()) || (s.trainer_1 || '').toLowerCase().includes(search.toLowerCase()))
    : sessions;

  const handleAdd = async () => {
    if (selected.size === 0) { toast.error('Select at least one session'); return; }
    setSaving(true);
    try {
      await pprwAPI.workshops.addSessions(workshopId, Array.from(selected));
      toast.success(`Added ${selected.size} session${selected.size !== 1 ? 's' : ''} — they inherited the workshop's common attributes`);
      onAdded(); onClose();
    } catch (_e) { toast.error('Failed to add sessions'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="add-sessions-dialog">
        <DialogHeader>
          <DialogTitle>Add Sessions to Workshop</DialogTitle>
          <DialogDescription>Only standalone sessions (not already in a workshop) are shown. Added sessions inherit the workshop's common attributes.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search sessions…" className="pl-9" data-testid="add-sessions-search" />
        </div>
        <div className="flex-1 overflow-y-auto rounded-lg border border-border mt-2">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-12">No standalone sessions available</p>
          ) : filtered.map(s => (
            <label key={s.id} className="flex items-center gap-3 px-3 py-2.5 border-b border-border/60 hover:bg-muted/40 cursor-pointer" data-testid={`add-session-row-${s.id}`}>
              <Checkbox checked={selected.has(s.id)} onCheckedChange={() => toggle(s.id)} data-testid={`add-session-check-${s.id}`} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{s.course_title}</p>
                <p className="text-xs text-muted-foreground">{fmtDate(s.start_date)} · {s.time_zone || '—'} · {s.trainer_1 || 'No trainer'}</p>
              </div>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleAdd} disabled={saving || selected.size === 0} data-testid="add-sessions-confirm-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Adding…</> : `Add ${selected.size || ''}`.trim()}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AddSessionsDialog;
